import type {
  GlbHierarchyNode,
  ImportedModelResult,
  ImportedModelTabDescriptor,
  PuppyImportedModelTabTemplate,
  PuppyPackageDocumentType,
} from '@puppycad/cad-core'
import { resolveOfflinePuppyTabType } from './resolveOfflinePuppyTabType'

/**
 * 离线包 tab 描述构建输入。
 */
export interface OfflinePuppyTabDescriptorBuildInput {
  /** cad-core 已完成解析后的模型结果。 */
  result: ImportedModelResult
  /** manifest 记录的 CAD 文档类型。 */
  documentType?: PuppyPackageDocumentType
  /** cad-core 根据 assembly.json 生成的虚拟 tab 模板。 */
  tabTemplates?: PuppyImportedModelTabTemplate[]
  /** 本次导入 root tab 的基础 ID。 */
  baseTabId: string
  /** 缺少 tab 模板时使用的兜底显示名称。 */
  fallbackLabel: string
  /** 判断 tab ID 是否已经被当前会话占用。 */
  isTabIdReserved: (tabId: string) => boolean
}

/**
 * 离线包 tab 描述构建结果。
 */
export interface OfflinePuppyTabDescriptorBuildResult {
  /** 本次导入的 root tab。 */
  rootDescriptor: ImportedModelTabDescriptor
  /** 本次导入应注册的全部 tab。 */
  descriptors: ImportedModelTabDescriptor[]
  /** tab ID 到 assembly.json 节点 ID 的映射，用于读取 cad-core 生成的真实 tab 结果。 */
  templateNodeIdByTabId: Record<string, string>
}

/**
 * 为本地离线包生成不会覆盖既有标签的 tab ID。
 *
 * @param baseTabId 基础 tab ID。
 * @param isTabIdReserved 已占用 tab ID 判断器。
 * @param reservedTabIds 本次构建内已经预占的 tab ID。
 * @returns 唯一 tab ID。
 */
function resolveUniqueOfflinePackageTabId(
  baseTabId: string,
  isTabIdReserved: (tabId: string) => boolean,
  reservedTabIds: ReadonlySet<string>,
): string {
  const normalizedBaseTabId = baseTabId.trim().length > 0
    ? baseTabId.trim()
    : `puppy:${Date.now()}`
  let candidate = normalizedBaseTabId
  let sequence = 2
  while (isTabIdReserved(candidate) || reservedTabIds.has(candidate)) {
    candidate = `${normalizedBaseTabId}#${sequence}`
    sequence += 1
  }
  return candidate
}

/**
 * 创建缺省离线包 tab 模板。
 *
 * @param documentType 离线包文档类型。
 * @param fallbackLabel 兜底 tab 显示名称。
 * @returns root tab 模板。
 */
function createFallbackPuppyTabTemplate(
  documentType: PuppyPackageDocumentType | undefined,
  fallbackLabel: string,
): PuppyImportedModelTabTemplate {
  const fallbackType = resolveOfflinePuppyTabType(documentType)
  return {
    nodeId: 'root',
    label: fallbackLabel,
    type: fallbackType,
    iconType: fallbackType,
    scopeObjectId: null,
    isRoot: true,
  }
}

/**
 * 让 root tab 固定排在第一位。
 *
 * @param templates 原始 tab 模板列表。
 * @returns root 优先的模板列表。
 */
function orderPuppyTabTemplates(
  templates: readonly PuppyImportedModelTabTemplate[],
): PuppyImportedModelTabTemplate[] {
  const rootTemplate = templates.find((template) => template.isRoot) ?? templates[0]
  if (!rootTemplate) {
    return []
  }
  return [
    rootTemplate,
    ...templates.filter((template) => template !== rootTemplate),
  ]
}

/**
 * 创建离线包虚拟 tab 描述。
 *
 * @param input tab 创建输入。
 * @returns root tab 与全部 tab 描述。
 */
export function createOfflinePuppyTabDescriptors(
  input: OfflinePuppyTabDescriptorBuildInput,
): OfflinePuppyTabDescriptorBuildResult {
  const templates = orderPuppyTabTemplates(
    input.tabTemplates && input.tabTemplates.length > 0
      ? input.tabTemplates
      : [createFallbackPuppyTabTemplate(input.documentType, input.fallbackLabel)],
  )
  if (templates.length === 0) {
    throw new Error('PuppyCAD 离线包缺少可创建 tab 的装配节点')
  }

  const reservedTabIds = new Set<string>()
  const rootTabId = resolveUniqueOfflinePackageTabId(input.baseTabId, input.isTabIdReserved, reservedTabIds)
  reservedTabIds.add(rootTabId)

  const templateNodeIdByTabId: Record<string, string> = {}
  const descriptors = templates.map((template, index) => {
    const tabId = index === 0
      ? rootTabId
      : resolveUniqueOfflinePackageTabId(`${rootTabId}::${template.nodeId}`, input.isTabIdReserved, reservedTabIds)
    reservedTabIds.add(tabId)
    templateNodeIdByTabId[tabId] = template.nodeId
    return {
      id: tabId,
      label: template.label,
      type: template.type,
      iconType: template.iconType,
      loading: false,
      sourceFileId: input.result.fileInfo.fileId,
      scopeObjectId: template.scopeObjectId,
    }
  })

  return {
    rootDescriptor: descriptors[0]!,
    descriptors,
    templateNodeIdByTabId,
  }
}

/**
 * 按 objectId 查找 hierarchy 子树。
 *
 * @param root hierarchy 根节点。
 * @param objectId 目标 Object3D UUID。
 * @returns 命中的 hierarchy 节点；未命中时返回 `null`。
 */
function findHierarchyNodeByObjectId(root: GlbHierarchyNode, objectId: string): GlbHierarchyNode | null {
  if (root.objectId === objectId) {
    return root
  }
  for (const child of root.children) {
    const found = findHierarchyNodeByObjectId(child, objectId)
    if (found) {
      return found
    }
  }
  return null
}

/**
 * 克隆 hierarchy 子树并把深度归零为当前 tab 的根。
 *
 * @param node 子树根节点。
 * @param parentObjectId 新父节点 ID。
 * @param depth 新深度。
 * @returns 归一化后的 hierarchy 子树。
 */
function cloneHierarchySubtreeForTab(
  node: GlbHierarchyNode,
  parentObjectId: string | null,
  depth: number,
): GlbHierarchyNode {
  return {
    ...node,
    parentObjectId,
    depth,
    children: node.children.map((child) => cloneHierarchySubtreeForTab(child, node.objectId, depth + 1)),
  }
}

/**
 * 根据虚拟 tab 作用域解析当前 tab 应展示的 hierarchy。
 *
 * @param rootHierarchy 离线包完整 hierarchy。
 * @param scopeObjectId tab 作用域对象 ID。
 * @returns 作用域内 hierarchy。
 */
export function resolveOfflinePuppyTabHierarchy(
  rootHierarchy: GlbHierarchyNode,
  scopeObjectId: string | null,
): GlbHierarchyNode {
  if (!scopeObjectId) {
    return rootHierarchy
  }
  const scopedNode = findHierarchyNodeByObjectId(rootHierarchy, scopeObjectId)
  if (!scopedNode) {
    throw new Error(`PuppyCAD 离线包 tab 缺少作用域 hierarchy: ${scopeObjectId}`)
  }
  return cloneHierarchySubtreeForTab(scopedNode, null, 0)
}
